import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CmsGallery from '../components/CmsGallery';
import CtaSection from '../components/CtaSection';
import { fetchCaseBySlug, getOptimizedImageUrl } from '../lib/cms';

export default function CaseDetailPage() {
  const { slug } = useParams();
  const [caseItem, setCaseItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCaseBySlug(slug).then((res) => {
      setCaseItem(res);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-40 pb-20 max-w-5xl mx-auto px-4 sm:px-6 animate-pulse space-y-8">
        <div className="h-6 w-32 bg-[#EAE4DC] rounded-full" />
        <div className="h-12 w-3/4 bg-[#EAE4DC] rounded-2xl" />
        <div className="aspect-16/9 bg-[#EAE4DC] rounded-3xl" />
      </div>
    );
  }

  if (!caseItem) {
    return (
      <div className="min-h-[70vh] pt-40 pb-20 max-w-4xl mx-auto px-4 text-center space-y-6">
        <h1 className="text-3xl font-bold text-[#161514]">Projektet blev ikke fundet</h1>
        <p className="text-sm text-[#5C5852]">Det efterspurgte projekt findes ikke eller er blevet opdateret.</p>
        <Link
          to="/cases"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#161514] text-white text-sm font-semibold"
        >
          <span>Tilbage til alle cases</span>
          <i className="fa-regular fa-arrow-left text-xs" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  return (
    <>
      <SeoHead
        title={caseItem.client_name}
        description={`Læs om serviceløsningen for ${caseItem.client_name} (${caseItem.industry}). Udfordring, løsning og resultater.`}
        ogImage={caseItem.cover_image?.[0]}
      />

      {/* Case Header */}
      <section className="pt-40 pb-16 md:pt-48 lg:pt-52 md:pb-24 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="flex items-center gap-3">
            <Link
              to="/cases"
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#7A746B] hover:text-[#161514] transition-colors"
            >
              <i className="fa-regular fa-arrow-left text-[10px]" aria-hidden="true" />
              <span>Alle cases</span>
            </Link>
            <span className="text-[#DDD6CC]">/</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#876F50]">
              {caseItem.industry}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#161514] leading-tight">
            {caseItem.client_name}
          </h1>

          <p className="text-lg text-[#5C5852] max-w-2xl leading-relaxed">
            {caseItem.challenge}
          </p>
        </div>
      </section>

      {/* Main Cover Image */}
      <section className="py-12 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-[#E5DFD6] aspect-16/9 bg-[#161514]">
            <img
              src={getOptimizedImageUrl(caseItem.cover_image?.[0], {
                width: 1400,
                quality: 85,
                format: 'auto',
              })}
              alt={caseItem.client_name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Detailed Breakdown */}
      <section className="py-16 md:py-24 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Udfordring */}
            <div className="bg-white border border-[#E5DFD6] rounded-3xl p-8 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#FAF8F5] text-[#876F50] flex items-center justify-center text-lg">
                <i className="fa-light fa-circle-question" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-bold text-[#161514]">Udfordring</h2>
              <p className="text-sm text-[#5C5852] leading-relaxed">
                {caseItem.challenge}
              </p>
            </div>

            {/* Løsning */}
            <div className="bg-white border border-[#E5DFD6] rounded-3xl p-8 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#FAF8F5] text-[#876F50] flex items-center justify-center text-lg">
                <i className="fa-light fa-lightbulb-on" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-bold text-[#161514]">Løsning</h2>
              <p className="text-sm text-[#5C5852] leading-relaxed">
                {caseItem.solution}
              </p>
            </div>

            {/* Content & Levering */}
            <div className="bg-white border border-[#E5DFD6] rounded-3xl p-8 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#FAF8F5] text-[#876F50] flex items-center justify-center text-lg">
                <i className="fa-light fa-sparkles" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-bold text-[#161514]">Udført service</h2>
              <p className="text-sm text-[#5C5852] leading-relaxed">
                {caseItem.content_produced}
              </p>
            </div>
          </div>

          {/* Result Block */}
          <div className="mt-8 bg-[#161514] text-white rounded-3xl p-8 sm:p-10 space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C5A880] font-semibold">
              <i className="fa-light fa-chart-line-up text-base" aria-hidden="true" />
              <span>Resultat & Merværdi</span>
            </div>
            <p className="text-lg sm:text-xl font-medium text-[#FAF8F5] leading-relaxed">
              {caseItem.result}
            </p>
          </div>
        </div>
      </section>

      {/* Case Gallery */}
      {caseItem.gallery_images && caseItem.gallery_images.length > 0 && (
        <section className="py-16 md:py-24 bg-[#FAFAF8] border-b border-[#E8E2D9]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#161514] tracking-tight">
                Visuelle øjeblikke fra opgaven
              </h2>
              <p className="text-sm text-[#5C5852]">
                Klik på billederne for at åbne dem i fuld skærm.
              </p>
            </div>

            <CmsGallery
              customImages={caseItem.gallery_images}
              title=""
              columns={3}
            />
          </div>
        </section>
      )}

      <CtaSection
        title={`Vil du have synlige resultater som ${caseItem.client_name}?`}
        subtitle="Hver forretning er forskellig – vi sammensætter en individuel aftale uden faste prispakker eller binding. Betal for 2 måneder og få 1 måned gratis!"
        primaryButtonText="Kontakt os for en snak"
        primaryButtonLink="/kontakt"
        secondaryButtonText="Se alle cases"
        secondaryButtonLink="/cases"
      />
    </>
  );
}
